import { Account } from 'src/services/auth/auth.types';
import { Setting } from 'src/services/settings/settings.types';

import { rootApi } from '../rootApi';
import { IPreferredLocation } from '../candidates/candidates.types';
import { APIResponse, HTTPRequestType, PaginatedResponse } from '../api.types';
import {
  AccountProfile,
  UpdateSettingProps,
  CreateAccountProfile,
  UpdateAccountPasswordProps,
} from './account.types';

const accountService = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getAccountDetails: builder.query<Pick<AccountProfile, 'id' | 'first_name' | 'last_name' | 'email' | 'profile_image'>, string>({
      query: (accountId) => ({
        url: `/accounts/${accountId}`,
      })
    }),
    getMyAccountProfile: builder.query<AccountProfile, void>({
      query: () => ({
        url: '/accounts/my/profile',
      }),
      providesTags: ['Account'],
    }),

    updateMyAccountProfile: builder.mutation<APIResponse, Partial<CreateAccountProfile>>({
      query: (data) => ({
        url: '/accounts/my/profile',
        method: HTTPRequestType.PUT,
        data,
      }),
      onQueryStarted: async (_arg, { dispatch }) => {
        setTimeout(() => {
          dispatch(rootApi.util.invalidateTags(['Profile', 'Account']));
        }, 10000);
      },
      invalidatesTags: ['Account', 'Profile'],
    }),

    updateMyAccountSpecializations: builder.mutation<APIResponse, { specializations: string[] }>({
      query: (data) => ({
        url: '/accounts/my/profile/specializations',
        method: HTTPRequestType.PUT,
        data,
      }),
      invalidatesTags: ['Account'],
    }),

    updateMyAccountLocations: builder.mutation<APIResponse, { locations: IPreferredLocation[] }>({
      query: (data) => ({
        url: '/accounts/my/profile/locations',
        method: HTTPRequestType.PUT,
        data,
      }),
      invalidatesTags: ['Account'],
    }),

    updateAccountPassword: builder.mutation<APIResponse, UpdateAccountPasswordProps>({
      query: (data) => ({
        url: '/accounts/my/password',
        method: HTTPRequestType.PUT,
        data,
      }),
    }),

    getAccountSettings: builder.query<Setting[], string>({
      query: (accountId: string) => ({
        url: `/accounts/${accountId}/settings`,
      }),
      providesTags: ['AccountSettings'],
    }),

    updateAccountSettings: builder.mutation<APIResponse, UpdateSettingProps>({
      query: (data) => ({
        url: `/accounts/${data.accountId}/settings`,
        method: HTTPRequestType.POST,
        data: {
          settings: data.settings,
        },
      }),
      invalidatesTags: ['AccountSettings'],
    }),

    deactivateAccount: builder.mutation<APIResponse, void>({
      query: (data) => ({
        url: `/accounts/my/deactivate`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['Account', 'Profile'],
    }),

    updateOnboardingContext: builder.mutation<APIResponse, { [key: string]: boolean }>({
      query: (data) => ({
        url: '/accounts/my/onboarding-context',
        data,
        method: HTTPRequestType.PUT,
      }),
      invalidatesTags: ['AuthContext'],
    }),
    updateAlertContext: builder.mutation<APIResponse, { [key: string]: boolean }>({
      query: (data) => ({
        url: '/accounts/my/alert-context',
        data,
        method: HTTPRequestType.PUT,
      }),
      invalidatesTags: ['AuthContext'],
    }),

    getMyAccounts: builder.query<PaginatedResponse<Account>, void>({
      query: () => ({
        method: HTTPRequestType.GET,
        url: '/accounts/list',
      }),
      providesTags: ['Accounts'],
    }),
    switchAccount: builder.mutation<APIResponse, { account_id: string }>({
      query: (data) => ({
        url: '/accounts/switch',
        method: HTTPRequestType.POST,
        data,
      })
    }),

    // phone verification
    requestPhoneVerification: builder.mutation<APIResponse, void>({
      query: (data) => ({
        url: '/accounts/my/phone-verification/request',
        method: HTTPRequestType.POST,
        data,
      }),
    }),
    verifyPhoneNumber: builder.mutation<APIResponse, { code: string }>({
      query: (data) => ({
        url: '/accounts/my/phone-verification/verify',
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: ['Account', 'Profile'],
    }),
    resendPhoneVerification: builder.mutation<APIResponse, void>({
      query: (data) => ({
        url: '/accounts/my/phone-verification/resend',
        method: HTTPRequestType.POST,
        data,
      }),
    }),
  }),
});

export const {
  useGetAccountDetailsQuery,
  useGetMyAccountProfileQuery,
  useUpdateMyAccountProfileMutation,
  useUpdateMyAccountSpecializationsMutation,
  useUpdateMyAccountLocationsMutation,
  useUpdateAccountPasswordMutation,
  useGetAccountSettingsQuery,
  useUpdateAccountSettingsMutation,
  useDeactivateAccountMutation,

  useUpdateOnboardingContextMutation,
  useUpdateAlertContextMutation,

  useGetMyAccountsQuery,
  useSwitchAccountMutation,

  useRequestPhoneVerificationMutation,
  useVerifyPhoneNumberMutation,
  useResendPhoneVerificationMutation,
} = accountService;
